import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  IconMusic, 
  IconAlertCircle, 
  IconPlus,
  IconEdit,
  IconTrash,
  IconSearch 
} from '@tabler/icons-react';
import { MainLayout } from '../components';
import { 
  StaffRequest, 
  StaffApplication,
  StaffType,
  MusicGenre,
  STAFF_TYPE_LABELS,
  MUSIC_GENRE_LABELS,
  CreateStaffRequestPayload
} from '../types/staff';
import { Event } from '../types/event';
import { staffService, eventService } from '../services/api';
import styles from './StaffRequestsPage.module.css';

const StaffRequestsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [staffRequests, setStaffRequests] = useState<StaffRequest[]>([]);
  const [applications, setApplications] = useState<StaffApplication[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<number | null>(null);
  
  // Form state
  const [formStaffType, setFormStaffType] = useState<StaffType>(StaffType.DJ);
  const [formRequiredGenres, setFormRequiredGenres] = useState<MusicGenre[]>([]);
  const [formDescription, setFormDescription] = useState('');
  const [formEquipmentProvided, setFormEquipmentProvided] = useState(false);
  const [formEquipmentDescription, setFormEquipmentDescription] = useState('');
  const [formBudget, setFormBudget] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const fetchData = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les infos de l'événement
      const eventData = await eventService.getById(parseInt(eventId));
      setEvent(eventData);
      
      // Récupérer les demandes de staff
      const requestsData = await staffService.getStaffRequests(parseInt(eventId));
      setStaffRequests(requestsData);
      
      // Récupérer les candidatures pour cet événement
      // Remarque: Nous devrons gérer cela différemment puisque nous récupérons désormais par requestId
      // Pour le moment, récupérons toutes les applications et filtrons côté client
      const allApplications = await staffService.getStaffApplications();
      // Filtrer pour ne garder que les applications liées à des requêtes de cet événement
      const applicationsForEvent = allApplications.filter(app => 
        requestsData.some(req => req.id === app.staffRequestId)
      );
      setApplications(applicationsForEvent);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur est survenue lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [eventId]);
  
  const handleGenreToggle = (genre: MusicGenre) => {
    if (formRequiredGenres.includes(genre)) {
      setFormRequiredGenres(formRequiredGenres.filter(g => g !== genre));
    } else {
      setFormRequiredGenres([...formRequiredGenres, genre]);
    }
  };
  
  const handleEditRequest = (request: StaffRequest) => {
    setCurrentRequestId(request.id);
    setFormStaffType(request.staffType);
    setFormRequiredGenres(request.requiredGenres || []);
    setFormDescription(request.description);
    setFormEquipmentProvided(request.equipmentProvided);
    setFormEquipmentDescription(request.equipmentDescription || '');
    setFormBudget(request.budget?.toString() || '');
    setShowAddForm(true);
  };
  
  const handleDeleteRequest = async (requestId: number) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette demande de staff ?")) {
      return;
    }
    
    try {
      await staffService.deleteStaffRequest(requestId);
      // Mettre à jour la liste des demandes
      setStaffRequests(staffRequests.filter(request => request.id !== requestId));
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur est survenue lors de la suppression");
      }
    }
  };
  
  const handleApplicationStatus = async (applicationId: number, status: 'ACCEPTED' | 'REJECTED') => {
    try {
      await staffService.updateApplicationStatus(applicationId, status);
      // Mettre à jour la liste des candidatures
      setApplications(applications.map(app => 
        app.id === applicationId 
          ? { ...app, status } 
          : app
      ));
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur est survenue lors de la mise à jour du statut");
      }
    }
  };
  
  const resetForm = () => {
    setCurrentRequestId(null);
    setFormStaffType(StaffType.DJ);
    setFormRequiredGenres([]);
    setFormDescription('');
    setFormEquipmentProvided(false);
    setFormEquipmentDescription('');
    setFormBudget('');
  };
  
  const handleAddFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!eventId) return;
    
    if (!formDescription) {
      setError("La description est requise");
      return;
    }
    
    try {
      setFormSubmitting(true);
      setError(null);
      
      const requestData: CreateStaffRequestPayload = {
        eventId: parseInt(eventId),
        staffType: formStaffType,
        requiredGenres: formRequiredGenres.length > 0 ? formRequiredGenres : undefined,
        description: formDescription,
        equipmentProvided: formEquipmentProvided,
        equipmentDescription: formEquipmentDescription || undefined,
        budget: formBudget ? parseFloat(formBudget) : undefined
      };
      
      if (currentRequestId) {
        // Mise à jour
        await staffService.updateStaffRequest({
          ...requestData,
          id: currentRequestId,
          isActive: true
        });
      } else {
        // Création
        await staffService.createStaffRequest(requestData);
      }
      
      resetForm();
      setShowAddForm(false);
      
      // Recharger les données
      fetchData();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur est survenue lors de l'enregistrement");
      }
    } finally {
      setFormSubmitting(false);
    }
  };
  
  const renderStaffRequests = () => {
    if (staffRequests.length === 0) {
      return (
        <div className={styles.emptyState}>
          <IconMusic size={48} />
          <p>Aucune demande de staff pour cet événement</p>
          <p className={styles.emptyDescription}>
            Ajoutez des demandes pour trouver des DJs, musiciens ou autres staff pour votre événement.
          </p>
          <button 
            className={styles.addButton}
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <IconPlus size={20} />
            Ajouter une demande de staff
          </button>
        </div>
      );
    }
    
    return (
      <div className={styles.requestsContainer}>
        <div className={styles.requestsHeader}>
          <h3 className={styles.requestsTitle}>Demandes de staff</h3>
          <button 
            className={styles.addButton}
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <IconPlus size={20} />
            Ajouter
          </button>
        </div>
        
        <div className={styles.requestsGrid}>
          {staffRequests.map(request => (
            <div key={request.id} className={styles.requestCard}>
              <div className={styles.requestHeader}>
                <h4 className={styles.requestType}>
                  {STAFF_TYPE_LABELS[request.staffType]}
                </h4>
                <div className={styles.requestActions}>
                  <button 
                    className={styles.iconButton}
                    onClick={() => handleEditRequest(request)}
                    title="Modifier"
                  >
                    <IconEdit size={18} />
                  </button>
                  <button 
                    className={styles.iconButton}
                    onClick={() => handleDeleteRequest(request.id)}
                    title="Supprimer"
                  >
                    <IconTrash size={18} />
                  </button>
                </div>
              </div>
              
              {request.requiredGenres && request.requiredGenres.length > 0 && (
                <div className={styles.requestGenres}>
                  {request.requiredGenres.map(genre => (
                    <span key={genre} className={styles.genreBadge}>
                      {MUSIC_GENRE_LABELS[genre]}
                    </span>
                  ))}
                </div>
              )}
              
              <div className={styles.requestDescription}>
                <p>{request.description}</p>
              </div>
              
              {request.equipmentProvided && (
                <div className={styles.equipmentInfo}>
                  <p className={styles.equipmentLabel}>Équipement fourni</p>
                  {request.equipmentDescription && (
                    <p className={styles.equipmentDescription}>{request.equipmentDescription}</p>
                  )}
                </div>
              )}
              
              {request.budget && (
                <div className={styles.budgetInfo}>
                  <p className={styles.budgetLabel}>Budget:</p>
                  <p className={styles.budgetValue}>{request.budget} €</p>
                </div>
              )}
              
              <div className={styles.applicationsCount}>
                <span>{request.applications?.length || 0} candidature(s)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderApplications = () => {
    if (applications.length === 0) {
      return (
        <div className={styles.emptyState}>
          <IconSearch size={48} />
          <p>Aucune candidature reçue pour le moment</p>
          <p className={styles.emptyDescription}>
            Les staffeurs intéressés par votre événement apparaîtront ici.
          </p>
        </div>
      );
    }
    
    return (
      <div className={styles.applicationsContainer}>
        <h3 className={styles.applicationsTitle}>Candidatures reçues</h3>
        
        <div className={styles.applicationsList}>
          {applications.map(app => (
            <div key={app.id} className={styles.applicationCard}>
              <div className={styles.applicationHeader}>
                <div className={styles.staffInfo}>
                  <h4 className={styles.staffType}>
                    {app.staffProfile && STAFF_TYPE_LABELS[app.staffProfile.staffType]}
                  </h4>
                  <div className={styles.applicationBadge} data-status={app.status.toLowerCase()}>
                    {app.status === 'PENDING' && 'En attente'}
                    {app.status === 'ACCEPTED' && 'Acceptée'}
                    {app.status === 'REJECTED' && 'Refusée'}
                  </div>
                </div>
                
                <div className={styles.staffName}>
                  {app.staffProfile?.user?.firstName} {app.staffProfile?.user?.lastName}
                </div>
              </div>
              
              {app.staffProfile?.genres && (
                <div className={styles.staffGenres}>
                  {renderGenres(app.staffProfile.genres)}
                </div>
              )}
              
              <div className={styles.applicationMessage}>
                <h5>Message du candidat:</h5>
                <p>{app.message}</p>
              </div>
              
              {app.status === 'PENDING' && (
                <div className={styles.applicationActions}>
                  <button 
                    className={styles.acceptButton}
                    onClick={() => handleApplicationStatus(app.id, 'ACCEPTED')}
                  >
                    Accepter
                  </button>
                  <button 
                    className={styles.rejectButton}
                    onClick={() => handleApplicationStatus(app.id, 'REJECTED')}
                  >
                    Refuser
                  </button>
                </div>
              )}
              
              <div className={styles.viewProfileAction}>
                <button 
                  className={styles.viewProfileButton}
                  onClick={() => navigate(`/staff/${app.staffProfileId}`)}
                >
                  Voir le profil complet
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderGenres = (genres: MusicGenre[] | string) => {
    if (typeof genres === 'string') {
      try {
        // Attempt to parse if it's a JSON string array
        const parsedGenres = JSON.parse(genres) as MusicGenre[];
        return parsedGenres.map((genre: MusicGenre) => (
          <span key={genre} className={styles.genreBadge}>
            {MUSIC_GENRE_LABELS[genre]}
          </span>
        ));
      } catch (e) {
        // If it's not valid JSON, just show the string
        return <span className={styles.genreBadge}>{genres}</span>;
      }
    } else if (Array.isArray(genres)) {
      return genres.map((genre: MusicGenre) => (
        <span key={genre} className={styles.genreBadge}>
          {MUSIC_GENRE_LABELS[genre]}
        </span>
      ));
    }
    return null;
  };
  
  const renderAddForm = () => {
    return (
      <div className={styles.formOverlay}>
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>
            <h3 className={styles.formTitle}>
              {currentRequestId 
                ? 'Modifier la demande de staff' 
                : 'Ajouter une demande de staff'
              }
            </h3>
            <button 
              className={styles.closeButton}
              onClick={() => {
                resetForm();
                setShowAddForm(false);
              }}
            >
              &times;
            </button>
          </div>
          
          {error && (
            <div className={styles.errorMessage}>
              <IconAlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          
          <form onSubmit={handleAddFormSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="staffType" className={styles.label}>Type de staff recherché</label>
              <select 
                id="staffType"
                className={styles.select}
                value={formStaffType}
                onChange={(e) => setFormStaffType(e.target.value as StaffType)}
                required
              >
                {Object.entries(STAFF_TYPE_LABELS).map(([type, label]) => (
                  <option key={type} value={type}>{label}</option>
                ))}
              </select>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>Genres musicaux requis (optionnel)</label>
              <div className={styles.genresGrid}>
                {Object.entries(MUSIC_GENRE_LABELS).map(([genre, label]) => (
                  <div key={genre} className={styles.genreItem}>
                    <label className={styles.genreLabel}>
                      <input
                        type="checkbox"
                        checked={formRequiredGenres.includes(genre as MusicGenre)}
                        onChange={() => handleGenreToggle(genre as MusicGenre)}
                        className={styles.genreCheckbox}
                      />
                      <span className={styles.genreName}>{label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>Description du poste</label>
              <textarea
                id="description"
                className={styles.textarea}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Décrivez le poste, les responsabilités, les attentes..."
                rows={4}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formEquipmentProvided}
                  onChange={(e) => setFormEquipmentProvided(e.target.checked)}
                  className={styles.checkbox}
                />
                <span>Équipement fourni par l'organisateur</span>
              </label>
            </div>
            
            {formEquipmentProvided && (
              <div className={styles.formGroup}>
                <label htmlFor="equipmentDescription" className={styles.label}>Description de l'équipement fourni</label>
                <textarea
                  id="equipmentDescription"
                  className={styles.textarea}
                  value={formEquipmentDescription}
                  onChange={(e) => setFormEquipmentDescription(e.target.value)}
                  placeholder="Décrivez l'équipement qui sera fourni..."
                  rows={3}
                />
              </div>
            )}
            
            <div className={styles.formGroup}>
              <label htmlFor="budget" className={styles.label}>Budget (optionnel)</label>
              <input
                id="budget"
                type="number"
                className={styles.input}
                value={formBudget}
                onChange={(e) => setFormBudget(e.target.value)}
                placeholder="Budget en euros"
                min="0"
                step="1"
              />
            </div>
            
            <div className={styles.formActions}>
              <button 
                type="button" 
                className={styles.cancelButton}
                onClick={() => {
                  resetForm();
                  setShowAddForm(false);
                }}
              >
                Annuler
              </button>
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={formSubmitting}
              >
                {formSubmitting 
                  ? 'Enregistrement...' 
                  : currentRequestId 
                    ? 'Mettre à jour' 
                    : 'Ajouter'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  return (
    <MainLayout>
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Chargement des données...</p>
          </div>
        ) : error && !showAddForm ? (
          <div className={styles.errorContainer}>
            <IconAlertCircle size={24} />
            <p>{error}</p>
          </div>
        ) : event ? (
          <>
            <div className={styles.header}>
              <h1 className={styles.title}>
                <IconMusic size={24} />
                Staff pour l'événement: {event.title}
              </h1>
              <p className={styles.subtitle}>
                Gérez les demandes de staff et les candidatures pour votre événement
              </p>
            </div>
            
            {renderStaffRequests()}
            {renderApplications()}
            
            {showAddForm && renderAddForm()}
          </>
        ) : (
          <div className={styles.errorContainer}>
            <IconAlertCircle size={24} />
            <p>Événement non trouvé</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default StaffRequestsPage;
